import React, { SyntheticEvent } from 'react';
import classNames from 'classnames';
import { useDropzone } from 'react-dropzone';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, ErrorMessage } from 'formik';
import * as yup from 'yup';

import Button, { ButtonType } from 'hellospa/foundation/control/button';
import { trackHeapCustomEvent } from 'common/utils/heap';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';
import { ConnectFormikToStepper } from 'hellospa/components/prep-and-send/formik-utils';
import useTriggers from 'hellospa/components/prep-and-send/trigger';
import { Dispatch } from 'hellospa/redux/types';
import { NotificationBannerType } from 'hellospa/components/notification-banner/data/types';
import FilesStatus from './files-status';
import TemplatePicker from '../template-picker';
import DropboxFilePicker from './integrations/dropbox';
import GoogleDriveFilePicker from './integrations/google-drive';
import OneDriveFilePicker from './integrations/one-drive';
import BoxFilePicker from './integrations/box';
import EvernoteFilePicker from './integrations/evernote';
import ReuseWarningModal from './template-reuse-warning-modal';
import styles from './styles.scss';
import dropboxIcon from 'images/v2/modules/home/integrations/dropbox_icon.svg';
import googleDriveIcon from 'images/v2/modules/home/integrations/drive_icon_@2x.png';
import boxIcon from 'images/v2/modules/home/integrations/box_icon_@2x.png';
import evernoteIcon from 'images/v2/modules/home/integrations/evernote_icon_@2x.png';
import oneDriveIcon from 'images/v2/modules/home/integrations/onedrive_icon_@2x.png';
import dragAndDropIcon from 'images/v2/modules/home/draganddrop_icon_@2x.png';
import { PageHeader, SectionHeader } from '../header';
import { useFeatureFlag } from 'hellospa/components/feature-flags';
import getFileErrorMessage from './messages';

const messages = defineMessages({
  reorder: {
    id: '',
    description: 'subheader for prep and send files list',
    defaultMessage: 'Drag and drop documents to reorder them',
  },
  docHeader: {
    id: '',
    description: 'header for prep and send files list',
    defaultMessage: "Documents you've selected",
  },
  nonEnterprisePlusBulkSendUpsell: {
    id: '',
    description: 'text upselling bulk send when a non Enterprise Plus customer attempts to use bulk send',
    defaultMessage: `Bulk send is available to Enterprise Plus customers and is the easiest
    way to send a single-signer template to many people. <a>Contact sales to learn more</a>
    `,
  },
});

/**
 * These messages are for an experiment (DEV-11504)
 *
 * By not calling defineMessages, these are hidden from Smartling and will
 * always fall back to English regardless of locale
 */
const experimentText = {
  greetingMessage: {
    // id wont generate without defineMessage
    id: 'first-time-experience-hello',
    defaultMessage: 'Hello{userFirstName} What needs to be signed?',
  },
  passwordUpdate: {
    id: 'password-update',
    defaultMessage: 'Your password has been set successfully.',
  },
};

type IntegrationState = { [key in types.UploadIntegrationType]: boolean };

const nonErroredFiles = yup.object({
  status: yup.mixed().oneOf([
    types.FilePollResponseStatus.Ok,
    types.FilePollResponseStatus.Converting,
  ]),
});
const requireFilesSchema = yup.object({
  files: yup.array(nonErroredFiles).required(),
});

function useFileBanners(files: types.UserFile[]) {
  const dispatch = useDispatch<Dispatch>();
  const { createBannerMessage } = actions.useLocalBanner();
  type BannerState = Record<types.UserFile['rootSnapshotGuid'], types.UserFile['status']>
  const [bannersShown, setBannersShown] = React.useState<BannerState>(() => (
    // Gather all of the initial file statuses so that this doesn't trigger
    // banners when returning to the upload page to fix the errors.
    files.reduce((state, file) => {
      state[file.rootSnapshotGuid] = file.status;
      return state;
    }, {} as BannerState)
  ));

  const isHelloFax = useSelector(selectors.isHelloFax);
  const isDeepIntegration = useSelector(selectors.isDeepIntegration);

  React.useEffect(() => {
    const erroredFiles = files.filter((file) => (
      types.isErrorStatus(file.status)
      && bannersShown[file.rootSnapshotGuid] !== file.status
    ));

    const newMessages = erroredFiles.reduce((state, file) => {
      // let message: ReturnType<typeof getFileErrorMessage> | null = null;
      const message = getFileErrorMessage(file.status, {
        useLong: isDeepIntegration,
        isHelloFax,
      });

      if (message) {
        createBannerMessage(
          message.descriptor,
          NotificationBannerType.Err,
          message.values,
        );
        return {
          ...state,
          [file.rootSnapshotGuid]: file.status,
        };
      }

      return state;
    }, null as null | BannerState);

    if (newMessages != null) {
      setBannersShown((state) => ({
        ...state,
        ...newMessages,
      }));
    }

  }, [bannersShown, createBannerMessage, dispatch, files, isDeepIntegration, isHelloFax]);

}

const FileUploader: types.WorkflowComponent = ({
  onSaveActions, formRef, triggers,
}) => {
  const dispatch = useDispatch();
  const files = useSelector(selectors.getFiles);
  const allowUpload = useSelector(selectors.allowUpload);
  const allowTemplate = useSelector(selectors.allowTemplate);
  const integrations = useSelector(selectors.getUploadIntegrations);
  const allowIntegrations = useSelector(selectors.allowIntegrationsUpload);
  const reuseWarning = useSelector(selectors.hasTemplateReuseWarning);
  const filter = useSelector(selectors.getTemplateFilter);
  const isBulkSend = useSelector(selectors.isBulkSend);
  const allowBulkSend = useSelector(selectors.allowBulkSend);
  useFileBanners(files);
  const { createBannerMessage } = actions.useLocalBanner();

  const isEarlyNextExperience = useFeatureFlag('HelloSign_Prep_Send_Early_Next');
  const isOptionalEditorExperience = useFeatureFlag('HelloFax_Optional_Editor');
  const shouldRouteToPrepAndSend = useSelector(selectors.shouldRouteToPrepAndSend);
  const intl = useIntl();
  const user = useSelector(selectors.getUser);
  const userFullName = user ? user.name : null;
  let userFirstName = userFullName ? userFullName.split(' ')[0] : '';
  userFirstName = userFirstName.trim() === '' ? '!' : ` ${userFirstName},`;

  const [Triggers] = useTriggers(triggers, {
    load: () => { },
    submit: () => { },
    done: () => { },
  });

  React.useEffect(() => {
    if (isEarlyNextExperience) {
      trackHeapCustomEvent('Added to P&S FeatureFlag', {
        'FeatureFlag Name': 'EarlyNext',
      });
    }
  }, [isEarlyNextExperience]);

  const onDrop = React.useCallback(async (files: File[]) => {
    try {
      for (let i = 0; i < files.length; i++) {
        // skipping await here, because this needs to be done concurrently
        // so the user doesn't see 1 file being uploaded at a time
        dispatch(actions.uploadFile(files[i]));
      }
    } catch (e) {
      throw new Error('Failed uploading files!');
    }
  }, [dispatch]);

  const [reuseWarningModalIsOpen, setReuseWarningModalIsOpen] = React.useState(false);

  const closeReuseWarningModal = () => {
    setReuseWarningModalIsOpen(false);
  };

  React.useEffect(() => {
    if (reuseWarning) {
      setReuseWarningModalIsOpen(reuseWarning);
    }
  }, [reuseWarning]);


  React.useEffect(() => {
    if (isBulkSend && !allowBulkSend) {
      const contactSalesUrl = '/info/pricing?contact_sales=1';
      // Users without bulk-send access shouldn't be able to access this flow anymore,
      // showing an upsell banner just in case
      createBannerMessage(
        messages.nonEnterprisePlusBulkSendUpsell,
        NotificationBannerType.Warning,
        {
          // eslint-disable-next-line react/display-name
          a: (msg) => <a href={contactSalesUrl}>{msg}</a>,
        },
      );
    }
  }, [createBannerMessage, isBulkSend, allowBulkSend]);
  const [templatePickerStatus, setTemplatePickerStatus] = React.useState(false);
  const toggleTemplatePicker = React.useCallback((status: boolean) => (): void => {
    setTemplatePickerStatus(status);
  }, []);

  const onSelectTemplate = React.useCallback((template: types.Template) => () => {
    dispatch(actions.useTemplate(template.id, isBulkSend));
    setTemplatePickerStatus(false);
  }, [dispatch, isBulkSend]);

  const submit = () => {
    trackHeapCustomEvent('Next clicked', {
      'Upload Complete': String(files.every(
        (f) => f.status === types.FilePollResponseStatus.Ok,
      )),
      'Number of files': files.length,
    });

    dispatch(actions.workflowOnSave(onSaveActions));
  };

  const loadOptionalEditor = async () => {
    await dispatch(actions.setWorkflow(types.workflows.SignatureRequestFax));
    await dispatch(actions.workflowOnSave(onSaveActions));
  };

  const [uploadIntegration, setUploadIntegration] = React.useState<IntegrationState>({
    [types.UploadIntegrationType.Dropbox]: false,
    [types.UploadIntegrationType.Evernote]: false,
    [types.UploadIntegrationType.OneDrive]: false,
    [types.UploadIntegrationType.GoogleDrive]: false,
    [types.UploadIntegrationType.Box]: false,
  });

  const toggleIntegrationPicker = React.useCallback((
    integration: string,
    status: boolean,
  ) => {
    setUploadIntegration((state) => ({
      ...state,
      [integration]: status,
    }));
  }, []);
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    // disabled if uploads aren't allowed, since now the whole
    // upload container allows files to be dropped onto it
    disabled: !allowUpload,
    // disable any clicks on the Dropzone container since all & any inner
    // elements bubble the event up to the parent triggering the picker
    noClick: true,
  });

  const addFileButton = allowUpload
    && (
      <span className={styles.uploadContainerSpan}>
        <input {...getInputProps()} data-qa-ref="upload-input" />
        <Button
          data-qa-ref="prep-upload-files"
          kind={ButtonType.Outline}
          onClick={open}
        >
          <FormattedMessage
            id=""
            description="button text for prep and send to upload files"
            defaultMessage="Upload file" />
        </Button>
      </span>
    );

  const dragAndDropContainer = allowUpload
    && <div className={classNames(
      styles.dragAndDropContainer,
      {
        [styles.dragActive]: isDragActive,
        [styles.dragAndDropContainerPadding]: shouldRouteToPrepAndSend,
      },
    )}
      data-qa-ref="prep-drag-drop-container"
      // allow the drag & drop container to trigger the file picker
      onClick={open}
    >
      <img src={dragAndDropIcon} />
      <div className={styles.dragAndDropText}>
        <FormattedMessage
          id=""
          description="text for prep and send to drag and drop files"
          defaultMessage="Drag and drop files here" />
      </div>
    </div>;

  const addTemplateButton = allowTemplate
    && <Button
      data-qa-ref="prep-add-template"
      role="button"
      disabled={isBulkSend && !allowBulkSend}
      kind={ButtonType.Outline}
      onClick={toggleTemplatePicker(true)}
    >
      <FormattedMessage
        id=""
        description="button text for prep and send to choose template"
        defaultMessage="Add template" />
    </Button>;

  // TODO: Clean up when DEV-12786 is done
  const updateTimer = React.useCallback((e: SyntheticEvent<HTMLElement>) => {
    if (e.target instanceof HTMLButtonElement
      || e.currentTarget instanceof HTMLButtonElement
      || e.target instanceof HTMLImageElement
    ) {
      const curTime = Date.now();
      dispatch(actions.updateNextClickTracker(curTime));
    }
  }, [dispatch]);

  return (

    <div
      data-qa-ref="files.uploader"
      className={classNames({ [styles.firstTimeExperience]: shouldRouteToPrepAndSend })}
      onClick={updateTimer}
    >
      <PageHeader>
        {shouldRouteToPrepAndSend
          ? intl.formatMessage(experimentText.greetingMessage, { userFirstName })
          : <FormattedMessage
            id=""
            description="text for documents page header"
            defaultMessage="What needs to be signed?" />
        }
      </PageHeader>
      <SectionHeader>
        <FormattedMessage
          id=""
          description="header for prep and send file upload page"
          defaultMessage="Select documents" />
      </SectionHeader>
      <div
        {...getRootProps()}
        className={classNames(
          styles.uploadContainer,
          {
            [styles.uploadContainerBorder]: allowUpload,
          },
        )}
      >
        <div className={classNames(
          styles.buttonsContainer,
          {
            [styles.noPadding]: !allowUpload && allowTemplate,
          },
        )}
        >
          {addFileButton}
          {!shouldRouteToPrepAndSend && addTemplateButton}
        </div>
        {!shouldRouteToPrepAndSend && allowIntegrations && (
          <div className={styles.integrationsContainer}>
            <div className={styles.integrationsText}>
              <FormattedMessage
                id=""
                description="text for prep and send for upload from integration"
                defaultMessage="Upload from" />
            </div>
            <div className={styles.integrationsIcons}>
              {integrations && integrations[types.UploadIntegrationType.Dropbox] && (
                <div>
                  <span
                    data-qa-ref="prep-dropbox-integration"
                    onClick={() => {
                      toggleIntegrationPicker(types.UploadIntegrationType.Dropbox, true);
                    }}
                  >
                    <img src={dropboxIcon} />
                  </span>
                  <DropboxFilePicker
                    // The condition to render this should guarantee that this is some value
                    settings={integrations[types.UploadIntegrationType.Dropbox]!}
                    shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.Dropbox]}
                    toggleIntegrationPicker={toggleIntegrationPicker} />
                </div>
              )}
              {integrations && integrations[types.UploadIntegrationType.GoogleDrive] && (
                <div>
                  <span
                    data-qa-ref="prep-google-drive-integration"
                    onClick={() => {
                      toggleIntegrationPicker(types.UploadIntegrationType.GoogleDrive, true);
                    }}
                  >
                    <img src={googleDriveIcon} />
                  </span>
                  <GoogleDriveFilePicker
                    // The condition to render this should guarantee that this is some value
                    settings={integrations[types.UploadIntegrationType.GoogleDrive]!}
                    shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.GoogleDrive]}
                    toggleIntegrationPicker={toggleIntegrationPicker} />
                </div>
              )}
              {integrations && integrations[types.UploadIntegrationType.Box] && (
                <div>
                  <span
                    data-qa-ref="prep-box-integration"
                    onClick={() => toggleIntegrationPicker(types.UploadIntegrationType.Box, true)}
                  >
                    <img src={boxIcon} />
                  </span>
                  <BoxFilePicker
                    // The condition to render this should guarantee that this is some value
                    settings={integrations[types.UploadIntegrationType.Box]!}
                    shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.Box]}
                    toggleIntegrationPicker={toggleIntegrationPicker} />
                </div>
              )}
              {integrations && integrations[types.UploadIntegrationType.Evernote] && (
                <div>
                  <span
                    data-qa-ref="prep-evernote-integration"
                    onClick={() => {
                      toggleIntegrationPicker(types.UploadIntegrationType.Evernote, true);
                    }}
                  >
                    <img src={evernoteIcon} />
                  </span>
                  <EvernoteFilePicker
                    // The condition to render this should guarantee that this is some value
                    settings={integrations[types.UploadIntegrationType.Evernote]!}
                    shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.Evernote]}
                    toggleIntegrationPicker={toggleIntegrationPicker} />
                </div>
              )}
              {integrations && integrations[types.UploadIntegrationType.OneDrive] && (
                <div>
                  <span
                    data-qa-ref="prep-one-drive-integration"
                    onClick={() => {
                      toggleIntegrationPicker(types.UploadIntegrationType.OneDrive, true);
                    }}
                  >
                    <img src={oneDriveIcon} />
                  </span>
                  <OneDriveFilePicker
                    // The condition to render this should guarantee that this is some value
                    settings={integrations[types.UploadIntegrationType.OneDrive]!}
                    shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.OneDrive]}
                    toggleIntegrationPicker={toggleIntegrationPicker} />
                </div>
              )}
            </div>
          </div>
        )}
        {dragAndDropContainer}
      </div>
      <SectionHeader
        subtext={intl.formatMessage(messages.reorder)}
        text={intl.formatMessage(messages.docHeader)} />
      <FilesStatus
        files={files} />
      {isOptionalEditorExperience && Array.isArray(files) && files.length > 0
      && <div>
        <Button
          data-qa-ref="hellofax-optional-editor"
          kind={ButtonType.Outline}
          onClick={loadOptionalEditor}
        >
          <FormattedMessage
            id=""
            description="button text for prep and send on Hellofax to let to see the editor"
            defaultMessage="Fill and Sign" />
        </Button>
      </div>
      }
      <TemplatePicker
        isOpen={templatePickerStatus}
        togglePicker={toggleTemplatePicker}
        onSelectTemplate={onSelectTemplate}
        filter={filter} />
      <ReuseWarningModal
        close={closeReuseWarningModal}
        isOpen={reuseWarningModalIsOpen} />

      <Formik
        initialValues={{ files }}
        onSubmit={() => submit()}
        validateOnChange={false}
        validationSchema={isEarlyNextExperience ? requireFilesSchema : types.multiUserFileSchema}
        innerRef={types.useFormikRef(formRef)}
        enableReinitialize={true}
        // eslint-disable-next-line react/no-children-prop
        children={() => (
          <Form>
            <Triggers />
            <ConnectFormikToStepper />
            <ErrorMessage
              name="files"
              component="span" />
          </Form>
        )} />
    </div>
  );
};

FileUploader.getText = () => {
  return defineMessages({
    title: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Select documents',
    },
    next: {
      id: '',
      description: 'text in next button',
      defaultMessage: 'Next',
    },
  });
};

export default FileUploader;
